import * as React from "react";
import { useCallback, useMemo, useEffect } from "react";
import styled from "styled-components";
import Immutable from "immutable";
import { Formik, Form, useFormikContext } from "formik";
import Direction from "views/logic/aggregationbuilder/Direction";
import { INVESTIGATION_ATTRIBUTES } from "security-app/components/Investigations/Constants";
import { Row, Col } from "components/bootstrap";
import DescriptionBox from "views/components/aggregationbuilder/DescriptionBox";
import SaveOrCancelButtons from "views/components/widgets/SaveOrCancelButtons";
import StickyBottomActions from "views/components/widgets/StickyBottomActions";
import WidgetModeConfiguration from "views/components/widgets/overview-configuration/WidgetModeConfiguration";
import SortConfiguration from "views/components/widgets/overview-configuration/SortConfiguration";
import ColumnsConfiguration from "views/components/widgets/overview-configuration/ColumnsConfiguration";
import FiltersConfiguration from "views/components/widgets/overview-configuration/filters/FiltersConfiguration";
import filterComponents from "./filters/filterComponents";
import InvestigationsWidgetConfig, { LIST_MODE, NUMBER_MODE } from "../logic/InvestigationsWidgetConfig";
import { SORT_DIRECTION_OPTIONS } from "../Constants";
const COLUMNS = Object.keys(INVESTIGATION_ATTRIBUTES);
const WIDGET_MODE_OPTIONS = [
  { label: "List", value: LIST_MODE },
  { label: "Count", value: NUMBER_MODE }
];
const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
`;
const Container = styled.div`
  height: 100%;

  .form-group {
    margin: 0 0 3px;

    &:last-child {
      margin: 0;
    }

    .control-label {
      padding-left: 0;
      padding-right: 0;
      padding-top: 5px;
      font-weight: normal;
      text-align: left;
      hyphens: auto;
    }
  }

  div[class^='col-'] {
    padding-right: 0;
    padding-left: 0;
  }
`;
const FullHeightRow = styled(Row)`
  height: 100%;
  padding-bottom: 15px;
  flex: 1;
`;
const FullHeightCol = styled(Col)`
  height: 100%;
`;
const SORTABLE_COLUMNS = Object.entries(INVESTIGATION_ATTRIBUTES).filter(([, { sortable }]) => sortable).map(([columnName]) => columnName);
const SubmitOnChange = () => {
  const { values, submitForm } = useFormikContext();
  useEffect(() => {
    submitForm();
  }, [values, submitForm]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null);
};
const InvestigationsWidgetEdit = ({
  children,
  onCancel,
  config,
  onChange
}) => {
  const initialValues = useMemo(
    () => ({
      mode: config.mode,
      filters: config.filters,
      fields: config.fields,
      sort: { field: config.sort.field, direction: config.sort.direction.direction }
    }),
    [config.fields, config.filters, config.sort.direction, config.sort.field, config.mode]
  );
  const _onSubmit = useCallback(
    ({ sort, mode, filters, fields }) => {
      const newSort = config.sort.toBuilder().field(sort.field).direction(Direction.fromJSON(sort.direction)).build();
      onChange(config.toBuilder().mode(mode).filters(filters).fields(fields).sort(newSort).build());
    },
    [config, onChange]
  );
  const columnTitle = useCallback((column) => {
    var _a, _b;
    return (_b = (_a = INVESTIGATION_ATTRIBUTES[column]) == null ? void 0 : _a.title) != null ? _b : column;
  }, []);
  return /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues,
      enableReinitialize: true,
      validateOnChange: true,
      validateOnMount: true,
      onSubmit: _onSubmit
    },
    ({ setValues, values }) => {
      const onChangeType = (newMode) => {
        if (newMode !== values.mode) {
          const defaultSort = {
            field: InvestigationsWidgetConfig.defaultSort.field,
            direction: InvestigationsWidgetConfig.defaultSort.direction.direction
          };
          if (newMode === NUMBER_MODE) {
            setValues({ ...values, fields: Immutable.OrderedSet(), sort: defaultSort });
          }
          if (newMode === LIST_MODE) {
            setValues({
              ...values,
              fields: InvestigationsWidgetConfig.defaultFields,
              sort: defaultSort
            });
          }
        }
      };
      return /* @__PURE__ */ React.createElement(StyledForm, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(SubmitOnChange, null), /* @__PURE__ */ React.createElement(FullHeightRow, null, /* @__PURE__ */ React.createElement(FullHeightCol, { md: 4, lg: 3 }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(StickyBottomActions, { actions: /* @__PURE__ */ React.createElement(SaveOrCancelButtons, { onCancel }), alignActionsAtBottom: true }, /* @__PURE__ */ React.createElement(DescriptionBox, { description: "Visualization" }, /* @__PURE__ */ React.createElement(WidgetModeConfiguration, { name: "mode", onChange: onChangeType, options: WIDGET_MODE_OPTIONS })), values.mode === LIST_MODE && /* @__PURE__ */ React.createElement(DescriptionBox, { description: "Columns" }, /* @__PURE__ */ React.createElement(
        ColumnsConfiguration,
        {
          columns: COLUMNS,
          createSelectPlaceholder: "Select a new column",
          name: "fields",
          columnTitle
        }
      )), /* @__PURE__ */ React.createElement(DescriptionBox, { description: "Filter" }, /* @__PURE__ */ React.createElement(
        FiltersConfiguration,
        {
          columnTitle,
          name: "filters",
          filterComponents
        }
      )), values.mode === LIST_MODE && /* @__PURE__ */ React.createElement(DescriptionBox, { description: "Sorting" }, /* @__PURE__ */ React.createElement(
        SortConfiguration,
        {
          columns: SORTABLE_COLUMNS,
          name: "sort",
          columnTitle,
          directions: SORT_DIRECTION_OPTIONS
        }
      ))))), /* @__PURE__ */ React.createElement(FullHeightCol, { md: 8, lg: 9 }, children)));
    }
  );
};
export default InvestigationsWidgetEdit;
